import React, { forwardRef, ReactNode } from 'react'
import { HandlerOf } from 'shared/helpers/typeHelper'
import styled from 'styled-components'

import { Button } from '../Button/Button'
import { Checkbox } from '../Checkbox/Checkbox'
import { TextInputProps } from '../TextInput/TextInput'
import { Dropdown, DropdownProps } from './Dropdown'
import { Label, Option } from './Dropdown.styles'
import { DropDownOpener } from './DropdownOpener'
import { SearchDropdown } from './SearchDropdown'

interface CheckboxDropdownOption {
  key?: string
  value: boolean
  label: string
  labelElement?: React.ReactNode
  disableCheckbox?: boolean
  Wrap?: React.ComponentType<any>
  onChange: HandlerOf<boolean>
}

interface CheckboxDropdownProps
  extends Omit<
    DropdownProps<string>,
    'renderOpener' | 'staticLabel' | 'error' | 'onChange' | 'options' | 'maxHeight' | 'width'
  > {
  placeholder?: string
  error?: React.ReactElement | string | boolean
  disableOptionFilter?: boolean
  options: CheckboxDropdownOption[]
  children?: React.ReactNode
  bottomAction?: React.ReactNode
  fontSize?: string
  maxHeight: number
  width: number | string
  itemSize?: number
  inputProps?: TextInputProps
  noSearchOption?: boolean
  onSelectAll?: HandlerOf<boolean>
}

const StyledMoreDropdownOptionWrap = styled(Option)`
  padding-top: 0;
  padding-bottom: 0;

  &:hover {
    background-color: inherit;
  }

  label {
    height: auto;
    align-items: center;
    overflow: hidden;
  }

  .MuiCheckbox-root,
  .MuiFormControlLabel-label {
    padding-top: 0;
    padding-bottom: 0;
  }

  .MuiFormControlLabel-label {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`

const OptionLabel = styled.span<{ $fontSize?: string }>`
  ${(p) => p.$fontSize && `font-size: ${p.$fontSize};`}
`

export const CheckboxDropdown = forwardRef(function CheckboxDropdown(props: CheckboxDropdownProps, ref: any) {
  const { options, children, error, fontSize, noSearchOption, onSelectAll } = props

  const checkboxOptions = options.map((option) => {
    const Wrap = option.Wrap || React.Fragment
    return {
      key: option.key,
      label: option.labelElement ?? option.label,
      value: option.label,
      renderer: () => (
        <StyledMoreDropdownOptionWrap
          key={option.key ?? option.label}
          className="checkbox-dropdown-option-wrap"
        >
          <Wrap>
            {option.disableCheckbox ? (
              option.labelElement ?? <OptionLabel $fontSize={fontSize}>{option.label}</OptionLabel>
            ) : (
              <Checkbox
                onChange={option.onChange}
                value={option.value}
                label={option.labelElement ?? <OptionLabel $fontSize={fontSize}>{option.label}</OptionLabel>}
              />
            )}
          </Wrap>
        </StyledMoreDropdownOptionWrap>
      ),
    }
  })

  const selectAllButton: ReactNode = onSelectAll ? (
    <Button
      variant="text"
      onClick={() => onSelectAll(options.filter((x) => x.value).length !== options.length)}
    >
      {options.filter((x) => x.value).length === options.length ? 'Clear all' : 'Select all'}
    </Button>
  ) : undefined

  if (children) {
    return (
      <DropDownOpener
        {...props}
        ref={ref}
        error={Boolean(error)}
        options={checkboxOptions}
        bottomAction={selectAllButton}
      >
        {() => children}
      </DropDownOpener>
    )
  }

  if (noSearchOption) {
    const selectedLabels = options.filter((x) => x.value).map((option) => option.label)

    return (
      <Dropdown
        {...props}
        options={checkboxOptions}
        bottomAction={selectAllButton}
        formatLabel={(label) => (
          <Label $isSelected={selectedLabels.length > 0}>
            {selectedLabels.length === options.length ? 'All' : selectedLabels.join(', ') || label}
          </Label>
        )}
      />
    )
  }

  return <SearchDropdown {...props} options={checkboxOptions} bottomAction={selectAllButton} />
})
